import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, X } from "lucide-react";
import { VectorMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content:
    "Hi — I'm VECTOR, the AI growth advisor at Vector Growth and Advisory. Ask me about acquisition funnels, AI + Salesforce CRM automation, or our market assessments — or tell me where your pipeline leaks.",
};

const CHIPS = ["What services do you offer?", "Tell me about the FIAT case", "How fast can results land?"];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(`s-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const [extracting, setExtracting] = useState(false);

  const prefill = async () => {
    if (extracting) return;
    setExtracting(true);
    try {
      const res = await fetch(`${API}/chat/extract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current }),
      });
      const data = await res.json();
      sessionStorage.setItem("vector_prefill", JSON.stringify(data));
      setOpen(false);
      navigate("/contact");
    } catch (e) {
      toast.error("Could not prefill the form — please use it directly.");
    } finally {
      setExtracting(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            if (j.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + j.delta };
                return c;
              });
            }
          } catch (e) { /* skip malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          content: "The advisor is offline right now — please use the contact form and the team will reply within one business day.",
        };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-launcher-button"
        aria-label={open ? "Close AI advisor" : "Open AI advisor"}
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full border border-neon/40 bg-surface/90 shadow-[0_0_35px_rgba(47,168,224,0.25)] backdrop-blur transition-transform duration-300 hover:scale-105"
      >
        {!open && <span className="absolute inset-0 animate-ping rounded-full border border-neon/30" />}
        {open ? <X className="h-5 w-5 text-neon" /> : <VectorMark className="h-7 w-7" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="glass fixed bottom-24 right-6 z-[60] flex h-[540px] w-[380px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border hairline shadow-2xl"
            data-testid="chatbot-panel"
          >
            <div className="flex items-center justify-between border-b hairline px-5 py-4">
              <div className="flex items-center gap-3">
                <VectorMark className="h-7 w-7" />
                <div className="leading-tight">
                  <p className="font-display text-sm font-bold tracking-wide text-ink">VECTOR — AI Advisor</p>
                  <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-neon/80">
                    <span className="h-1.5 w-1.5 animate-blink rounded-full bg-neon" /> Online — grounded on Vector knowledge
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chatbot-close-button"
                aria-label="Close chat"
                className="flex h-8 w-8 items-center justify-center rounded-full border hairline text-slate-500 transition-colors hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] rounded-xl px-4 py-3 text-[13px] leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-azure-deep text-white"
                      : "border hairline bg-elevated text-slate-600"
                  }`}
                  data-testid={`chatbot-message-${m.role}`}
                >
                  {m.content || (
                    <span className="flex gap-1 py-1" data-testid="chatbot-typing-indicator">
                      <span className="h-1.5 w-1.5 animate-blink rounded-full bg-neon" />
                      <span className="h-1.5 w-1.5 animate-blink rounded-full bg-neon [animation-delay:0.2s]" />
                      <span className="h-1.5 w-1.5 animate-blink rounded-full bg-neon [animation-delay:0.4s]" />
                    </span>
                  )}
                </div>
              ))}
              {messages.length <= 2 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      data-testid="chatbot-quick-chip"
                      className="rounded-full border hairline bg-elevated/60 px-3 py-1.5 text-[11px] text-slate-600 transition-colors hover:border-neon/50 hover:text-neon"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              {messages.length > 2 && !streaming && (
                <button
                  onClick={prefill}
                  disabled={extracting}
                  data-testid="chatbot-prefill-button"
                  className="inline-flex rounded-full border border-neon/40 bg-neon/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neon transition-all duration-300 hover:bg-neon hover:text-white disabled:opacity-50"
                >
                  {extracting ? "Drafting your enquiry…" : "Prefill contact form with my answers →"}
                </button>
              )}
              {messages.length > 2 && (
                <Link
                  to="/contact"
                  data-testid="chatbot-contact-link"
                  className="inline-flex rounded-full border hairline px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 transition-colors hover:text-neon"
                >
                  Talk to the team → /contact
                </Link>
              )}
            </div>

            <div className="border-t hairline p-4">
              <div className="flex items-center gap-2 rounded-full border border-slate-300 bg-white py-1 pl-4 pr-1">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about your growth…"
                  data-testid="chatbot-input"
                  className="flex-1 bg-transparent py-2 text-[13px] text-ink placeholder:text-slate-600 focus:outline-none"
                />
                <button
                  onClick={() => send()}
                  disabled={streaming || !input.trim()}
                  data-testid="chatbot-send-button"
                  aria-label="Send message"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-azure-deep text-white transition-opacity disabled:opacity-40"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
